import React, { useState, useRef, useEffect } from 'react';
import '../../styles/SearchableSelect.css';

const SearchableSelect = ({ 
    options, 
    value, 
    onChange, 
    placeholder, 
    labelKey = 'name',
    valueKey = 'id',
    className = '',
    showCodeInParentheses = true
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isWide, setIsWide] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
                setIsWide(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownStyle({
                position: 'fixed',
                top: `${rect.bottom + window.scrollY}px`,
                left: `${rect.left + window.scrollX}px`,
                minWidth: `${rect.width}px`,
                width: isWide ? '400px' : `${rect.width}px`
            });
        }
    }, [isOpen, isWide]);

    const filteredOptions = options.filter(option => {
        const searchValue = searchTerm.toLowerCase();
        const optionText = option[labelKey].toLowerCase();
        return optionText.includes(searchValue);
    });

    const selectedOption = options.find(opt => opt[valueKey] === value);

    // Funktion til at formatere visningen af en option
    const formatOptionLabel = (option) => {
        const label = option[labelKey];
        const id = option[valueKey];
        
        // Hvis showCodeInParentheses er false, returner kun labelen
        if (!showCodeInParentheses) {
            return label;
        }
        
        // Hvis labelen allerede indeholder parenteser, antager vi det er formateret korrekt
        if (label.includes('(')) {
            return label;
        }
        
        // Ellers formaterer vi det pænt med ID'et i parentes
        return `${label.split(' (')[0]} (${id})`;
    };

    const handleOptionClick = (e, option) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(option[valueKey]);
        setIsOpen(false);
        setSearchTerm('');
        setIsWide(false);
    };

    const handleSearchFocus = () => {
        // Forsink breddeændringen lidt så den ikke interfererer med click events
        setTimeout(() => setIsWide(true), 50);
    };

    return (
        <div className={`searchable-select ${className}`} ref={wrapperRef}>
            <div 
                className="select-header" 
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                    if (!isOpen) {
                        setIsWide(false);
                    }
                }}
                ref={inputRef}
            >
                <input
                    type="text"
                    placeholder={placeholder}
                    value={selectedOption ? formatOptionLabel(selectedOption) : ''}
                    className="select-input"
                    readOnly
                />
                <span className="select-arrow">▼</span>
            </div>
            
            {isOpen && (
                <div 
                    className={`select-dropdown ${isWide ? 'active' : ''}`}
                    style={dropdownStyle}
                    onClick={e => e.stopPropagation()}
                >
                    <input
                        type="text"
                        placeholder="Søg..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={handleSearchFocus}
                        className="search-input"
                        autoFocus
                        onClick={e => e.stopPropagation()}
                    />
                    <div className="options-list">
                        {filteredOptions.map(option => (
                            <div
                                key={option[valueKey]}
                                className={`option ${value === option[valueKey] ? 'selected' : ''}`}
                                onClick={(e) => handleOptionClick(e, option)}
                            >
                                {formatOptionLabel(option)}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect; 